import React, { useState, useEffect, useRef } from 'react';

// Waypoint types for dropdown
const WAYPOINT_TYPES = [
    // UTMS-focused command set
    'WAYPOINT', 'SURVEY', 'TAKEOFF', 'LOITER_TIME', 'CHANGE_SPEED',
    'CONDITION_DELAY', 'LAND', 'RTL'
];

// Param labels per waypoint type (like Mission Planner)
const PARAM_CONFIG = {
    TAKEOFF:    { param1: 'Pitch °' },
    LOITER_TIME:  { param1: 'Time s' },
    CHANGE_SPEED: { param1: 'Type', param2: 'Speed m/s', param3: 'Throttle' },
    CONDITION_DELAY: { param1: 'Delay s' },
    WAYPOINT: { param1: 'Hold s' },
    SURVEY: { param1: 'Hold s' },
    LAND: {},
    RTL: {},
};

// Longer hints kept as tooltips so the row stays compact
const PARAM_HINTS = {
    CHANGE_SPEED: { param1: '0=Horizontal, 1=Groundspeed, 2=Up, 3=Down', param3: '-1 keeps current throttle' },
};

// Types that don't require location coordinates
const NON_LOCATION_TYPES = ['RTL', 'CONDITION_DELAY', 'CHANGE_SPEED'];

// Types only allowed on the last waypoint
const LAST_ONLY_TYPES = ['RTL', 'LAND'];

// Phone sheet: collapsed bar / open height (also published as --m-bottom-lift)
const MOBILE_BAR_HEIGHT = 48;
const MOBILE_OPEN_HEIGHT = 'min(42dvh, 340px)';

const EmDash = () => <span style={{ color: 'var(--textMuted)', fontSize: '11px', opacity: 0.6 }}>—</span>;

function SpinnerInput({ value, step, onCommit, width, fontSize = '12px' }) {
    return (
        <div className="waypoint-input-wrapper" style={width ? { width } : undefined}>
            <input
                type="number"
                className="wp-field"
                value={value}
                onChange={(e) => onCommit(e.target.value)}
                step={step}
                style={{ fontSize }}
                onClick={(e) => e.stopPropagation()}
            />
            <div className="waypoint-spinner-btns">
                <button
                    type="button"
                    className="waypoint-spinner-btn"
                    tabIndex={-1}
                    onClick={(e) => { e.stopPropagation(); onCommit((parseFloat(value) || 0) + parseFloat(step)); }}
                >▲</button>
                <button
                    type="button"
                    className="waypoint-spinner-btn"
                    tabIndex={-1}
                    onClick={(e) => { e.stopPropagation(); onCommit((parseFloat(value) || 0) - parseFloat(step)); }}
                >▼</button>
            </div>
        </div>
    );
}

function BottomWaypointPanel({ waypoints, onUpdateWaypoint, onDeleteWaypoint, onReorderWaypoint, isVisible, onToggle, selectedWaypointId, onSelectWaypoint, isMobileLayout = false }) {
    const selectedRowRef = useRef(null);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Auto-scroll to selected row
    useEffect(() => {
        if (selectedWaypointId && selectedRowRef.current) {
            selectedRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [selectedWaypointId]);

    // Phone: tell the voice FAB / zoom / planner panel how much of the bottom
    // this sheet takes, so they sit above it instead of on it.
    useEffect(() => {
        if (!isMobileLayout) return undefined;
        const root = document.documentElement.style;
        root.setProperty('--m-bottom-lift', isVisible ? `calc(${MOBILE_OPEN_HEIGHT} + 8px)` : `${MOBILE_BAR_HEIGHT + 8}px`);
        return () => root.removeProperty('--m-bottom-lift');
    }, [isMobileLayout, isVisible]);

    // Helper to handle input changes
    const handleChange = (id, field, value) => {
        let finalValue = value;
        if (field === 'latitude' || field === 'longitude' || field === 'altitude') {
            finalValue = parseFloat(value);
        }
        onUpdateWaypoint(id, { [field]: finalValue });
    };

    const isHomeWp = (wp) => !!wp && (!!wp.isHome || String(wp.name || '').toUpperCase() === 'HOME');
    const typeOptions = (wp, index) => (WAYPOINT_TYPES.includes(wp.type) ? [] : [wp.type]).concat(WAYPOINT_TYPES.filter(t => {
        // RTL and LAND are only allowed on the last waypoint
        if (LAST_ONLY_TYPES.includes(t) && index !== waypoints.length - 1) return false;
        return true;
    })).map(t => (
        <option key={t} value={t}>
            {WAYPOINT_TYPES.includes(t) ? t.replace(/_/g, ' ') : `${t} (LEGACY)`}
        </option>
    ));

    if (isMobileLayout) {
        const numField = (wp, key, label, step) => (
            <label key={key} className="m-wp-field">
                <span>{label}</span>
                <input type="number" inputMode="decimal" step={step} value={wp[key] !== undefined ? wp[key] : 0}
                    onChange={(e) => handleChange(wp.id, key, e.target.value)} onClick={(e) => e.stopPropagation()} />
            </label>
        );
        return (
            <div className="m-wp-sheet" style={{ height: isVisible ? MOBILE_OPEN_HEIGHT : MOBILE_BAR_HEIGHT }}>
                <button type="button" className="m-b m-wp-bar" onClick={onToggle}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--m-cyan)' }}>list_alt</span>
                    <span className="m-wp-bar-title">WAYPOINT EDITOR</span>
                    <span className="m-wp-bar-count">{waypoints.length} POINTS</span>
                    <span className="m-wp-bar-toggle">
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{isVisible ? 'expand_more' : 'expand_less'}</span>
                        {isVisible ? 'HIDE' : 'EDITOR'}
                    </span>
                </button>
                {isVisible && (
                    <div className="m-wp-list">
                        {waypoints.length === 0 && (
                            <div className="m-empty">
                                <span className="material-symbols-outlined" style={{ fontSize: 28, display: 'block', opacity: 0.5, marginBottom: 6 }}>pin_drop</span>
                                No waypoints yet — tap the map to add points
                            </div>
                        )}
                        {waypoints.map((wp, index) => {
                            const isHome = isHomeWp(wp);
                            const isSelected = wp.id === selectedWaypointId;
                            const noLocation = NON_LOCATION_TYPES.includes(wp.type);
                            const config = PARAM_CONFIG[wp.type] || {};
                            const canUp = index > 0 && !isHomeWp(waypoints[index - 1]);
                            return (
                                <div
                                    key={wp.id}
                                    ref={isSelected ? selectedRowRef : null}
                                    className={`m-wp${isSelected ? ' selected' : ''}`}
                                    onClick={() => onSelectWaypoint && onSelectWaypoint(wp.id)}
                                >
                                    <div className="m-wp-top">
                                        <span className="m-wp-idx">{isHome ? 'H' : index + 1}</span>
                                        {isHome ? (
                                            <span className="m-wp-home">HOME</span>
                                        ) : (
                                            <select className="m-wp-type" value={wp.type} onChange={(e) => handleChange(wp.id, 'type', e.target.value)} onClick={(e) => e.stopPropagation()}>
                                                {typeOptions(wp, index)}
                                            </select>
                                        )}
                                        {!isHome && (
                                            <span className="m-wp-actions" onClick={(e) => e.stopPropagation()}>
                                                <button type="button" className="m-b m-icon-btn" disabled={!canUp} onClick={() => onReorderWaypoint && onReorderWaypoint(index, index - 1)} aria-label="Move up">
                                                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_upward</span>
                                                </button>
                                                <button type="button" className="m-b m-icon-btn" disabled={index === waypoints.length - 1} onClick={() => onReorderWaypoint && onReorderWaypoint(index, index + 1)} aria-label="Move down">
                                                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_downward</span>
                                                </button>
                                                <button type="button" className="m-b m-icon-btn danger" onClick={() => onDeleteWaypoint(wp.id)} aria-label="Delete waypoint">
                                                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    {(!noLocation || Object.keys(config).length > 0) && (
                                        <div className="m-wp-fields">
                                            {!noLocation && numField(wp, 'latitude', 'LAT', '0.00001')}
                                            {!noLocation && numField(wp, 'longitude', 'LON', '0.00001')}
                                            {!noLocation && numField(wp, 'altitude', 'ALT m', '1')}
                                            {Object.keys(config).map((key) => numField(wp, key, config[key], '1'))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Drag handlers
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());

        // Create custom drag image
        const dragImage = document.createElement('div');
        dragImage.textContent = `Waypoint ${index + 1}`;
        dragImage.style.cssText = 'position: absolute; top: -1000px; background: var(--accentSecondary); color: var(--textPrimary); padding: 8px 16px; border-radius: 8px; font-size: 12px; font-weight: 600;';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 0, 0);
        setTimeout(() => document.body.removeChild(dragImage), 0);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== dropIndex && onReorderWaypoint) {
            onReorderWaypoint(draggedIndex, dropIndex);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    return (
        <div style={{
            position: 'absolute',
            bottom: isVisible ? '10px' : '0',
            left: isVisible ? '10px' : '0',
            right: isVisible ? '10px' : '0',
            height: isVisible ? (isMobileLayout ? '220px' : '288px') : '40px',
            background: 'var(--bgSecondary)',
            borderTop: '2px solid var(--accent)',
            borderRadius: isVisible ? '12px' : '0',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1000,
            boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.55)',
            overflow: 'hidden'
        }}>
            {/* Header / Toggle Bar */}
            <div
                onClick={onToggle}
                style={{
                    height: '40px',
                    flexShrink: 0,
                    background: 'color-mix(in srgb, var(--accent) 5%, transparent)',
                    borderBottom: isVisible ? '1px solid var(--borderSecondary)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 16px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'background 0.2s'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                        width: '24px', height: '24px', borderRadius: '6px',
                        background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <span className="material-symbols-outlined" style={{ color: 'var(--accent)', fontSize: '16px' }}>route</span>
                    </div>
                    <div style={{
                        color: 'var(--textPrimary)',
                        fontWeight: 700,
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        fontFamily: "'JetBrains Mono', monospace"
                    }}>
                        Waypoint Editor
                    </div>
                    <span style={{
                        background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
                        color: 'var(--accent)',
                        padding: '2px 10px',
                        borderRadius: '999px',
                        fontSize: '10px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        fontFamily: "'JetBrains Mono', monospace"
                    }}>
                        {waypoints.length} PTS
                    </span>
                </div>
                <div style={{
                    color: 'var(--textSecondary)',
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.8px',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px'
                }}>
                    {isVisible ? 'Hide' : 'Show'}
                    <span className="material-symbols-outlined" style={{
                        fontSize: '18px',
                        transition: 'transform 0.25s',
                        transform: isVisible ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}>keyboard_arrow_up</span>
                </div>
            </div>

            {/* Editor Content */}
            {isVisible && (
                <div style={{ flex: 1, overflow: 'auto', padding: '0 6px 6px', background: 'var(--bgSecondary)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: 'var(--textPrimary)' }}>
                        <thead style={{ position: 'sticky', top: 0, background: 'var(--bgSecondary)', zIndex: 10, boxShadow: '0 1px 0 var(--borderSecondary)' }}>
                            <tr>
                                <th style={{ ...headerStyle, width: '34px' }}></th>
                                <th style={{ ...headerStyle, width: '36px', textAlign: 'center' }}>#</th>
                                <th style={{ ...headerStyle, width: '138px' }}>Type</th>
                                <th style={{ ...headerStyle, width: '15%' }}>Latitude</th>
                                <th style={{ ...headerStyle, width: '15%' }}>Longitude</th>
                                <th style={{ ...headerStyle, width: '96px' }}>Alt (m)</th>
                                <th style={headerStyle}>Params</th>
                                <th style={{ ...headerStyle, width: '56px', textAlign: 'right' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {waypoints.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ padding: '48px 40px', textAlign: 'center', color: 'var(--textMuted)' }}>
                                        <span className="material-symbols-outlined" style={{ fontSize: '32px', opacity: 0.5, display: 'block', marginBottom: '10px' }}>pin_drop</span>
                                        <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '1px', marginBottom: '6px', fontFamily: "'JetBrains Mono', monospace" }}>NO WAYPOINTS DEFINED</div>
                                        <div style={{ fontSize: '12px', opacity: 0.8 }}>Click on the map to add points to your mission</div>
                                    </td>
                                </tr>
                            ) : (
                                waypoints.map((wp, index) => {
                                    const isSelected = wp.id === selectedWaypointId;
                                    const isDragging = draggedIndex === index;
                                    const isDragOver = dragOverIndex === index;
                                    const isHome = isHomeWp(wp);
                                    const noLocation = NON_LOCATION_TYPES.includes(wp.type);
                                    return (
                                        <tr
                                            key={wp.id}
                                            ref={isSelected ? selectedRowRef : null}
                                            onClick={() => onSelectWaypoint && onSelectWaypoint(wp.id)}
                                            style={{
                                                borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 4%, transparent)',
                                                background: isDragOver ? 'rgba(59, 130, 246, 0.3)' : (isSelected ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'transparent'),
                                                borderLeft: isSelected ? '2px solid var(--accent)' : '2px solid transparent',
                                                opacity: isDragging ? 0.5 : 1,
                                                transition: 'background 0.12s',
                                                cursor: 'pointer',
                                                borderTop: isDragOver ? '2px solid var(--accentSecondary)' : 'none'
                                            }}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onMouseEnter={(e) => {
                                                if (!isSelected && !isDragOver) e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 5%, transparent)';
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!isSelected && !isDragOver) e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            <td
                                                draggable={!isHome}
                                                onDragStart={(e) => {
                                                    if (isHome) {
                                                        e.preventDefault();
                                                        return;
                                                    }
                                                    handleDragStart(e, index);
                                                }}
                                                onDragEnd={handleDragEnd}
                                                onClick={(e) => e.stopPropagation()}
                                                style={{
                                                    ...cellStyle,
                                                    textAlign: 'center',
                                                    cursor: isHome ? 'not-allowed' : 'grab',
                                                    color: isHome ? 'var(--accent)' : 'var(--textMuted)',
                                                    padding: '6px 4px'
                                                }}
                                                title={isHome ? 'HOME waypoint stays at top' : 'Drag to reorder'}
                                            >
                                                <span className="material-symbols-outlined" style={{ fontSize: '16px', pointerEvents: 'none' }}>
                                                    {isHome ? 'home' : 'drag_indicator'}
                                                </span>
                                            </td>
                                            <td style={{
                                                ...cellStyle,
                                                textAlign: 'center',
                                                color: isSelected ? 'var(--accent)' : 'var(--textMuted)',
                                                fontWeight: 700,
                                                fontSize: '11px'
                                            }}>
                                                {isHome ? 'H' : index + 1}
                                            </td>
                                            <td style={cellStyle}>
                                                {isHome ? (
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '5px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        letterSpacing: '0.5px',
                                                        color: 'var(--accent)',
                                                        background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                                                        fontFamily: "'JetBrains Mono', monospace"
                                                    }}>
                                                        HOME
                                                    </div>
                                                ) : (
                                                    <select
                                                        className="wp-field wp-select"
                                                        value={wp.type}
                                                        onChange={(e) => handleChange(wp.id, 'type', e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {typeOptions(wp, index)}
                                                    </select>
                                                )}
                                            </td>
                                            <td style={cellStyle}>
                                                {noLocation ? <EmDash /> : (
                                                    <SpinnerInput
                                                        value={wp.latitude}
                                                        step="0.00001"
                                                        onCommit={(v) => handleChange(wp.id, 'latitude', v)}
                                                    />
                                                )}
                                            </td>
                                            <td style={cellStyle}>
                                                {noLocation ? <EmDash /> : (
                                                    <SpinnerInput
                                                        value={wp.longitude}
                                                        step="0.00001"
                                                        onCommit={(v) => handleChange(wp.id, 'longitude', v)}
                                                    />
                                                )}
                                            </td>
                                            <td style={cellStyle}>
                                                {noLocation ? <EmDash /> : (
                                                    <SpinnerInput
                                                        value={wp.altitude}
                                                        step="1"
                                                        onCommit={(v) => handleChange(wp.id, 'altitude', v)}
                                                    />
                                                )}
                                            </td>
                                            {/* Contextual Params */}
                                            <td style={cellStyle}>
                                                {(() => {
                                                    const config = PARAM_CONFIG[wp.type] || {};
                                                    const hints = PARAM_HINTS[wp.type] || {};
                                                    const paramKeys = Object.keys(config);
                                                    if (paramKeys.length === 0) return <EmDash />;
                                                    return (
                                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                                            {paramKeys.map(key => (
                                                                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }} title={hints[key] || ''}>
                                                                    <label style={{
                                                                        fontSize: '8.5px',
                                                                        color: 'var(--textMuted)',
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        whiteSpace: 'nowrap',
                                                                        fontFamily: "'JetBrains Mono', monospace"
                                                                    }}>{config[key]}</label>
                                                                    <SpinnerInput
                                                                        value={wp[key] !== undefined ? wp[key] : 0}
                                                                        step="1"
                                                                        onCommit={(v) => handleChange(wp.id, key, v)}
                                                                        width="84px"
                                                                        fontSize="11px"
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'right' }}>
                                                {isHome ? (
                                                    <span
                                                        className="material-symbols-outlined"
                                                        title="HOME waypoint is locked"
                                                        style={{ fontSize: '16px', color: 'var(--textMuted)', opacity: 0.6 }}
                                                    >lock</span>
                                                ) : (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDeleteWaypoint(wp.id);
                                                        }}
                                                        style={deleteButtonStyle}
                                                        title="Delete Waypoint"
                                                        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--danger)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--danger) 12%, transparent)'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--textMuted)'; e.currentTarget.style.background = 'transparent'; }}
                                                    >
                                                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>delete</span>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Styles
const headerStyle = {
    textAlign: 'left',
    padding: '9px 10px',
    borderBottom: '1px solid var(--borderSecondary)',
    color: 'var(--textMuted)',
    fontWeight: 700,
    fontSize: '9.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    fontFamily: "'JetBrains Mono', monospace"
};

const cellStyle = {
    padding: '7px 10px',
    color: 'var(--textPrimary)',
    fontFamily: "'JetBrains Mono', monospace",
    verticalAlign: 'middle'
};

const deleteButtonStyle = {
    background: 'transparent',
    color: 'var(--textMuted)',
    border: 'none',
    borderRadius: '6px',
    padding: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    transition: 'all 0.15s'
};

export default BottomWaypointPanel;
